<template>
  <div class="date-range-display-input input-group" :class="{ minimal: props.minimal, small: props.minimal, 'graph-style': props.graphStyle }">
    <span v-if="props.prependIcon || slots['prepend']" class="input-group-text">
      <slot name="prepend">
        <datetime-icon class="date-time-icon" />
      </slot>
    </span>
    <div v-else class="date-range-display-input__prepend-blank"></div>

    <!-- case 1: no start-end date selected, show placeholder -->
    <template v-if="!props.startValue && !props.endValue">
      <div class="date-range-display-input__placeholder">{{ props.placeholder }}</div>
    </template>
    <!-- case 2: relative selected -->
    <template v-else-if="props.relative">
      <div class="date-range-display-input__relative-value">{{ props.relativeLabel }}</div>
    </template>
    <!-- case 3: custom date selected -->
    <template v-else>
      <input type="text" class="form-control" :placeholder="props.startPlaceholder" :value="startValue" readonly :style="`color: ${colorStyle}`" />
      <span class="input-group-text" :style="`color: ${colorStyle}`">{{ separator }}</span>
      <input type="text" class="form-control" :placeholder="props.endPlaceholder" :value="endValue" readonly :style="`color: ${colorStyle}`" />
    </template>

    <span v-if="slots['append']" class="input-group-text">
      <slot name="append"></slot>
    </span>
    <div v-else class="date-range-display-input__append-blank"></div>
  </div>
</template>

<script setup lang="ts">
import DatetimeIcon from '@/assets/icons/base/datetime-picker/DatetimeIcon.vue';
import { Prop, useSlots } from 'vue';

const slots = useSlots();

const props = defineProps({
  // model start-end date (formatted string from ChocoDateRangePicker)
  startValue: <Prop<string | null>>{ default: () => null },
  endValue: <Prop<string | null>>{ default: () => null },
  // labels
  relative: <Prop<boolean>>{ type: Boolean, default: () => false },
  relativeLabel: <Prop<string | null>>{ default: () => null },
  // placeholders
  placeholder: <Prop<string>>{ default: 'Select Date' },
  startPlaceholder: <Prop<string>>{ default: 'Start Date' },
  endPlaceholder: <Prop<string>>{ default: 'End Date' },
  // styles
  separator: <Prop<string>>{ default: 'to' },
  size: <Prop<'small' | 'normal'>>{ default: 'normal' },
  minimal: <Prop<boolean>>{ type: Boolean, default: () => false },
  prependIcon: <Prop<boolean>>{ type: Boolean, default: () => false },
  graphStyle: <Prop<boolean>>{ type: Boolean, default: () => false },
  colorStyle: <Prop<string>>{ default: 'grey' },
});
</script>

<style scoped lang="scss">
$input-box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);

.date-range-display-input {
  height: 40px;
  border: 1px solid #ededed;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: $input-box-shadow;
  }

  .input-group-text {
    background-color: unset;
    border: unset;
    border-radius: 10px;
    padding: 0 8px;
  }

  .form-control {
    border: none;
    background-color: unset;
    cursor: pointer;
    padding-left: 0;
    padding-right: 0;
    text-align: center;

    &:focus {
      box-shadow: none;
    }
  }

  .input-group-text {
    color: #545454;
    fill: #545454;
  }

  .date-range-display-input__prepend-blank {
    padding-left: 18px;
  }

  .date-range-display-input__append-blank {
    padding-right: 18px;
  }

  .date-range-display-input__relative-value,
  .date-range-display-input__placeholder {
    display: flex;
    flex-grow: 1;
    padding-left: 6px;
    align-items: center;
  }

  .date-range-display-input__placeholder {
    color: gray;
  }

  &.minimal {
    display: inline-flex;
    width: auto;
    height: auto;
    border: unset;
    justify-content: space-between;

    .date-range-display-input__prepend-blank,
    .date-range-display-input__append-blank {
      padding-left: 0;
      padding-right: 0;
    }

    .input-group-text {
      padding: 0 2px;
    }

    .form-control {
      padding: 2px 0;
    }
  }

  &.small {
    .form-control {
      font-size: 14px;
    }
  }

  &.graph-style {
    font-size: 14px;
    padding: 2px 0 2px 1px;
    border-radius: 6px;

    &:hover {
      color: #007fff;
      fill: #007fff;
      background-color: #e5f2ff;
      box-shadow: none;

      .date-range-display-input__placeholder {
        color: #5eacfa;
      }

      .form-control,
      .input-group-text {
        color: #007fff;
        fill: #007fff;

        &::placeholder {
          color: #5eacfa;
        }
      }
    }
  }
}

.choco-input-popup.popup-show {
  .date-range-display-input {
    box-shadow: $input-box-shadow;

    &.graph-style {
      color: #007fff;
      fill: #007fff;
      background-color: #e5f2ff;
      box-shadow: none;

      .date-range-display-input__placeholder {
        color: #5eacfa;
      }

      .form-control,
      .input-group-text {
        color: #007fff;
        fill: #007fff;

        &::placeholder {
          color: #5eacfa;
        }
      }
    }
  }
}

.date-time-icon {
  width: 20px;
  height: 20px;
}
</style>
